import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { makeRequest } from "../../../utils/axios.js"; 
import { useEffect } from "react";

export const useCommentActions = (
    comment, 
    queryClient, 
    postId
) => {
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyDesc, setReplyDesc] = useState("");
    const [replyLogicType, setReplyLogicType] = useState("CLARIFICATION");
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [isFetchingPreview, setIsFetchingPreview] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        let timer;
        if (cooldown > 0) {
            timer = setInterval(() => setCooldown((prev) => prev - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [cooldown]);

    const likeMutation = useMutation({
        mutationFn: () => makeRequest.put("/comments/like/" + comment.id),
        onSuccess: () => queryClient.invalidateQueries(["comments", postId]),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => makeRequest.delete("/comments/" + id),
        onSuccess: () => {
            queryClient.invalidateQueries(["comments", postId]);
            setIsDeleteModalOpen(false); 
        },
    });

    const replyMutation = useMutation({
        mutationFn: (newComment) => makeRequest.post("/comments", newComment),
        onSuccess: () => {
            queryClient.invalidateQueries(["comments", postId]);
            setReplyOpen(false);
            setReplyDesc("");
            setReplyLogicType("CLARIFICATION");
        },
        onError: (err) => {
            const status = err.response?.status;
            if (status === 429) {
                alert("You're responding too quickly. Please slow down!");
                setCooldown(15);
            } else if (status === 409) {
                alert("You just sent a similar answer!");
                setCooldown(5);
            } else {
                console.error("Error sending reply:", err);
            }
        }
    });

    const handleLike = () => likeMutation.mutate();

    const handleReplySubmit = async () => {
        if (!replyDesc.trim() || cooldown > 0 || replyMutation.isPending || isFetchingPreview) return;

        setIsFetchingPreview(true);
        let linkPayload = {};

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const matches = replyDesc.match(urlRegex);
        const url = matches ? matches[0] : null;

        if (url) {
            try {
                const res = await makeRequest.post("/posts/preview", { url });
                if (res.data) {
                    linkPayload = {
                        linkUrl: res.data.url || url,
                        linkTitle: res.data.title,
                        linkDesc: res.data.description,
                        linkImg: res.data.image || res.data.img 
                    };
                }
            } catch (err) {
                console.log("Không lấy được preview link:", err);
                linkPayload = { linkUrl: url };
            }
        }

        replyMutation.mutate({
            desc: replyDesc,
            postId,
            parentCommentId: comment.id,
            logicType: replyLogicType,
            ...linkPayload
        });

        setIsFetchingPreview(false);
    };

    const handleDeleteClick = (e) => {
        e.preventDefault(); 
        e.stopPropagation();  
        setMenuOpen(false);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        deleteMutation.mutate(comment.id);
    };

    const cancelDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDeleteModalOpen(false);
    };

    return {
        state: { 
            replyOpen, 
            replyDesc, 
            replyLogicType,
            isDeleteModalOpen, 
            cooldown, 
            isPending: replyMutation.isPending,
            menuOpen,
        },
        actions: { 
            setReplyOpen, 
            setReplyDesc, 
            setReplyLogicType,
            handleLike, 
            handleDeleteClick,
            confirmDelete, 
            cancelDelete,
            handleReplySubmit,
            setMenuOpen, 
        },
        isPending: replyMutation.isPending
    };
};